// En uppföljning (ADR 0072): utkastet, som byrån kan ändra och godkänna eller
// stoppa. Pushnotisen i appen leder hit för samma uppföljning.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiFel, data } from '../api/client.js';
import { useAuth } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';
import {
  dagOchTid,
  kallaText,
  kund,
  orsakText,
  statusChip,
  statusText,
  type Uppfoljning,
} from '../uppfoljning/texter.js';

export function UppfoljningPost() {
  const { id = '' } = useParams();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const fid = useId();

  const post = useQuery({
    queryKey: ['uppfoljning', id],
    queryFn: async () =>
      data(await api.GET('/v1/admin/follow-ups/{id}', { params: { path: { id } } })),
    retry: (antal, fel) => !(fel instanceof ApiFel && fel.status === 404) && antal < 2,
  });

  const [rubrik, setRubrik] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [kvitto, setKvitto] = useState<string | null>(null);

  useEffect(() => {
    if (post.data && rubrik === null) {
      setRubrik(post.data.subject ?? '');
      setText(post.data.body ?? '');
    }
  }, [post.data, rubrik]);

  const klar = (svar: Uppfoljning, meddelande: string) => {
    queryClient.setQueryData(['uppfoljning', id], svar);
    void queryClient.invalidateQueries({ queryKey: ['uppfoljningar'] });
    setKvitto(meddelande);
  };

  const godkann = useMutation({
    mutationFn: async () => {
      const u = post.data;
      const andradRubrik = rubrik !== null && rubrik.trim() !== (u?.subject ?? '');
      const andradText = text !== null && text.trim() !== (u?.body ?? '');
      return data(
        await api.POST('/v1/admin/follow-ups/{id}/approve', {
          params: { path: { id } },
          body: {
            ...(andradRubrik && rubrik ? { subject: rubrik.trim() } : {}),
            ...(andradText && text ? { body: text.trim() } : {}),
          },
        }),
      );
    },
    onSuccess: (svar) => klar(svar, 'Uppföljningen är godkänd och skickas till kunden.'),
  });

  const stoppa = useMutation({
    mutationFn: async () =>
      data(await api.POST('/v1/admin/follow-ups/{id}/cancel', { params: { path: { id } } })),
    onSuccess: (svar) => klar(svar, 'Uppföljningen är stoppad och skickas inte.'),
  });

  const brodsmulor = (
    <nav className="brodsmulor hjalp" aria-label="Brödsmulor">
      <Link to="/uppfoljning">Uppföljning</Link>
    </nav>
  );

  if (post.isError) {
    const saknas = post.error instanceof ApiFel && post.error.status === 404;
    return (
      <Sida titel={saknas ? 'Uppföljningen finns inte' : 'Uppföljning'} brodsmulor={brodsmulor}>
        <div className="meddelande" role="alert">
          {saknas
            ? 'Uppföljningen finns inte, eller hör till en annan byrå.'
            : 'Uppföljningen gick inte att hämta. Ladda om sidan och försök igen.'}
        </div>
      </Sida>
    );
  }
  const u = post.data;
  if (!u) {
    return (
      <Sida titel="Uppföljning" brodsmulor={brodsmulor}>
        <p role="status">Hämtar uppföljningen …</p>
      </Sida>
    );
  }

  const vantar = u.status === 'awaiting_approval';
  const kanStoppas = vantar || u.status === 'scheduled';
  const upptagen = godkann.isPending || stoppa.isPending;
  const giltig = (rubrik ?? '').trim().length > 0 && (text ?? '').trim().length > 0;
  const fel = godkann.error ?? stoppa.error;

  return (
    <Sida
      titel={`Uppföljning av offert ${u.quote?.number ?? ''}`.trim()}
      brodsmulor={brodsmulor}
      bredvid={<span className={`chip ${statusChip(u.status)}`}>{statusText(u.status)}</span>}
    >
      <dl className="bedomning-lista uppfoljning-fakta kort flikkort">
        <div>
          <dt>Till</dt>
          <dd>{kund(u)}</dd>
        </div>
        {u.sender && (
          <div>
            <dt>Från</dt>
            <dd>
              {u.sender.name} &lt;{u.sender.email}&gt;
            </dd>
          </div>
        )}
        <div>
          <dt>{u.status === 'sent' ? 'Skickad' : u.status === 'cancelled' ? 'Stoppad' : 'Dags'}</dt>
          <dd>
            {dagOchTid(
              u.status === 'sent' && u.sentAt
                ? u.sentAt
                : u.status === 'cancelled' && u.cancelledAt
                  ? u.cancelledAt
                  : u.dueAt,
            )}
          </dd>
        </div>
        {u.quote && (
          <div>
            <dt>Offert</dt>
            <dd>
              <Link to={`/forfragningar/${u.quote.inquiryId}`}>{u.quote.number}</Link>
            </dd>
          </div>
        )}
      </dl>

      {u.status === 'cancelled' && <p className="ingress">{orsakText(u.cancelReason)}</p>}

      {u.status === 'scheduled' && !u.subject ? (
        <div className="kort tom">
          <p>AI skriver utkastet när det är dags. Ni kan stoppa uppföljningen innan dess.</p>
        </div>
      ) : (
        <section className="kort flikkort" aria-labelledby={`${fid}-mejl`}>
          <div className="resultat-status">
            <h2 id={`${fid}-mejl`} className="avsnitt-rubrik">
              Mejlet
            </h2>
            {u.draftSource && <span className="hjalp">{kallaText(u.draftSource)}</span>}
          </div>
          {vantar ? (
            <>
              <div className="falt">
                <label className="etikett" htmlFor={`${fid}-rubrik`}>
                  Ämnesrad
                </label>
                <input
                  id={`${fid}-rubrik`}
                  className="in"
                  maxLength={200}
                  value={rubrik ?? ''}
                  onChange={(e) => {
                    setKvitto(null);
                    setRubrik(e.target.value);
                  }}
                />
              </div>
              <div className="falt">
                <label className="etikett" htmlFor={`${fid}-text`}>
                  Text
                </label>
                <textarea
                  id={`${fid}-text`}
                  className="in"
                  rows={10}
                  maxLength={5000}
                  value={text ?? ''}
                  onChange={(e) => {
                    setKvitto(null);
                    setText(e.target.value);
                  }}
                />
                <div className="hjalp">
                  Länken till offerten och länken för att avböja fler mejl läggs till automatiskt.
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="mejl-rubrik">{u.subject}</p>
              <p className="mejl-text">{u.body}</p>
            </>
          )}
        </section>
      )}

      {fel && (
        <div className="meddelande" role="alert">
          {fel instanceof ApiFel && fel.status === 409
            ? 'Uppföljningen har redan skickats eller stoppats. Ladda om sidan.'
            : 'Det gick inte. Försök igen om en stund.'}
        </div>
      )}
      <div className="levande" aria-live="polite">
        {kvitto && <div className="kvitto">{kvitto}</div>}
      </div>
      {kanStoppas && (
        <div className="panel-fot">
          <button
            type="button"
            className="btn btn-g farlig"
            disabled={upptagen}
            onClick={() => {
              if (window.confirm('Stoppa uppföljningen? Den skickas aldrig till kunden.'))
                stoppa.mutate();
            }}
          >
            Stoppa uppföljningen
          </button>
          {vantar && (
            <button
              type="button"
              className="btn btn-p"
              disabled={upptagen || !giltig}
              onClick={() => godkann.mutate()}
            >
              {godkann.isPending ? 'Skickar …' : 'Godkänn och skicka'}
            </button>
          )}
        </div>
      )}
    </Sida>
  );
}
